import React from "react";
import { Check, Droplets, Wind, Gauge } from "lucide-react";

const SERVICES = [
  {
    id: "car-wash",
    icon: Droplets,
    name: "Car Wash",
    price: "$3",
    priceNote: "3 minutes",
    duration: "3 min",
    features: [
      "High-pressure spray wand",
      "Foam brush applicator",
      "Tire & wheel cleaner",
      "Pre-soak & rinse modes",
      "Wax rinse option",
    ],
    comingSoon: false,
    cta: "Get Washing",
  },
  {
    id: "vacuum",
    icon: Wind,
    name: "Vacuum",
    price: "$2",
    priceNote: "2.5 minutes",
    duration: "2.5 min",
    features: [
      "High-powered vacuum",
      "Multiple nozzle attachments",
      "Interior freshener spray",
      "Window cleaning station",
      "Coin & card accepted",
    ],
    comingSoon: false,
    cta: "Start Vacuuming",
  },
  {
    id: "air-compressor",
    icon: Gauge,
    name: "Air Compressor",
    price: "—",
    priceNote: "coming soon",
    duration: "",
    features: [
      "Tire inflation",
      "Precise pressure control",
      "All valve types supported",
      "Digital pressure gauge",
      "Quick & easy to use",
    ],
    comingSoon: true,
    cta: "Coming Soon",
  },
];

export default function Services() {
  const scrollToBooking = () => {
    const el = document.querySelector("#booking");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="services" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#a8d8ea]/30 text-[#1a2744] rounded-full px-4 py-2 text-sm font-bold mb-4 border border-[#a8d8ea]/40">
            <Droplets size={15} strokeWidth={2.5} className="text-[#1a2744]" />
            Self-Service Stations
          </div>
          <h2
            className="text-4xl sm:text-5xl font-bold text-[#1a2744]"
            style={{ fontFamily: "Fredoka, sans-serif" }}
            data-testid="services-heading"
          >
            Pick Your Station
          </h2>
          <p className="text-[#1a2744]/60 text-base sm:text-lg mt-3 max-w-md mx-auto">
            Simple, flat-rate pricing. Pull up, pay, and get to work.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {SERVICES.map((service) => {
            const Icon = service.icon;
            return (
              <div
                key={service.id}
                className={`relative rounded-3xl p-8 flex flex-col transition-all duration-300 ${
                  service.comingSoon
                    ? "bg-[#f0f9ff] border border-[#a8d8ea]/30 opacity-80"
                    : "bg-white border border-[#a8d8ea]/50 shadow-[0_8px_30px_rgba(26,39,68,0.07)] hover:-translate-y-2 hover:shadow-xl"
                }`}
                data-testid={`service-card-${service.id}`}
              >
                {service.comingSoon && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#1a2744] text-white text-xs font-bold px-6 py-1.5 rounded-full shadow-lg whitespace-nowrap">
                    Coming Soon
                  </div>
                )}

                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-6 ${
                    service.comingSoon ? "bg-[#a8d8ea]/20" : "bg-[#a8d8ea]/30"
                  }`}
                >
                  <Icon size={22} strokeWidth={2.5} className="text-[#1a2744]" />
                </div>

                <h3
                  className="text-2xl font-bold text-[#1a2744] mb-1"
                  style={{ fontFamily: "Fredoka, sans-serif" }}
                >
                  {service.name}
                </h3>
                <p className="text-sm font-semibold text-[#1a2744]/50 mb-4">
                  {service.priceNote}
                </p>

                <div
                  className="mb-8"
                  data-testid={`service-price-${service.id}`}
                >
                  {service.comingSoon ? (
                    <span className="text-3xl font-bold text-[#1a2744]/40" style={{ fontFamily: "Fredoka, sans-serif" }}>
                      Coming Soon
                    </span>
                  ) : (
                    <div className="flex items-end gap-2">
                      <span className="text-5xl font-bold text-[#1a2744]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                        {service.price}
                      </span>
                      <span className="text-base font-semibold text-[#1a2744]/50 mb-2">/ {service.duration}</span>
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3">
                      <div
                        className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                          service.comingSoon ? "bg-[#a8d8ea]/30" : "bg-[#a8d8ea]/60"
                        }`}
                      >
                        <Check size={11} strokeWidth={3} className="text-[#1a2744]" />
                      </div>
                      <span className={`text-sm font-medium ${service.comingSoon ? "text-[#1a2744]/45" : "text-[#1a2744]/75"}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={service.comingSoon ? undefined : scrollToBooking}
                  disabled={service.comingSoon}
                  className={`w-full rounded-full py-3.5 font-bold text-sm transition-all duration-300 ${
                    service.comingSoon
                      ? "bg-[#a8d8ea]/20 text-[#1a2744]/40 cursor-not-allowed border border-[#a8d8ea]/30"
                      : "bg-[#a8d8ea] text-[#1a2744] hover:-translate-y-0.5 hover:bg-[#7fc8e0] hover:shadow-lg"
                  }`}
                  data-testid={`service-book-button-${service.id}`}
                >
                  {service.cta}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-sm text-[#1a2744]/45 mt-10 font-medium">
          All stations accept coins and credit/debit cards. No account needed.
        </p>
      </div>
    </section>
  );
}
